import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import Reveal from "@/components/base/Reveal";
import usePageMeta from "@/hooks/usePageMeta";
import { newsList, newsTypeLabel, newsTypes, type NewsType } from "@/mocks/news";
import { works } from "@/mocks/works";

const PAGE_SIZE = 5;

type YearFilter = "all" | "2024" | "2023";

const yearOptions: { value: YearFilter; label: string }[] = [
  { value: "all", label: "全部时间" },
  { value: "2024", label: "2024 年" },
  { value: "2023", label: "2023 年" },
];

export default function News() {
  usePageMeta({
    title: "翔田千里动态资讯列表－翔田千里最新影视动态与访谈报道",
    description:
      "翔田千里最新动态资讯，汇总影视作品公告、媒体访谈与活动报道。可按资讯类型与年份筛选，按发布时间倒序浏览，点击任一标题进入资讯详情。",
    keywords: "翔田千里动态,翔田千里资讯,演员访谈,活动报道,影视新闻",
  });

  const [searchParams] = useSearchParams();
  const [type, setType] = useState<NewsType | "all">("all");
  const [year, setYear] = useState<YearFilter>("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    const typeParam = searchParams.get("type");
    setType(
      typeParam === "news" || typeParam === "interview" || typeParam === "event" ? typeParam : "all",
    );
    setVisibleCount(PAGE_SIZE);
  }, [searchParams]);

  const filtered = useMemo(() => {
    return newsList
      .filter((item) => {
        if (type !== "all" && item.type !== type) return false;
        if (year !== "all" && !item.date.startsWith(year)) return false;
        return true;
      })
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [type, year]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visible.length;

  const reset = () => {
    setType("all");
    setYear("all");
    setVisibleCount(PAGE_SIZE);
  };

  const featuredWorks = works.slice(0, 2);
  const allTags = useMemo(() => {
    const counts = new Map<string, number>();
    newsList.forEach((item) => {
      item.tags.forEach((tag) => counts.set(tag, (counts.get(tag) || 0) + 1));
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, []);

  return (
    <SiteLayout>
      <section className="border-b border-background-200/70 bg-background-50">
        <div className="container-page py-9 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "动态资讯" }]} />
          <div className="mt-6 max-w-2xl">
            <h1 className="font-heading text-3xl leading-tight text-foreground-950 md:text-[40px]">
              翔田千里最新动态
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              汇总影视作品公告、媒体访谈与活动报道，保持平均每月一次更新。全部资讯按发布时间倒序排列，可按资讯类型或年份筛选查看。
            </p>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-8 md:py-10">
        <div className="container-page grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px] lg:gap-8">
          <div>
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-4">
              <div className="flex flex-col gap-4">
                <div>
                  <p className="mb-2 text-[11px] uppercase tracking-wider2 text-foreground-400">资讯类型</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setType("all");
                        setVisibleCount(PAGE_SIZE);
                      }}
                      className={`cursor-pointer whitespace-nowrap rounded-md border px-3 py-1.5 text-xs transition-colors ${
                        type === "all"
                          ? "border-primary-600 bg-primary-600 text-background-50"
                          : "border-background-300/70 bg-background-50 text-foreground-700 hover:border-primary-300"
                      }`}
                    >
                      全部
                    </button>
                    {newsTypes.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => {
                          setType(item.value);
                          setVisibleCount(PAGE_SIZE);
                        }}
                        className={`cursor-pointer whitespace-nowrap rounded-md border px-3 py-1.5 text-xs transition-colors ${
                          type === item.value
                            ? "border-primary-600 bg-primary-600 text-background-50"
                            : "border-background-300/70 bg-background-50 text-foreground-700 hover:border-primary-300"
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-200/70 pt-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] text-foreground-400">发布日期</span>
                    {yearOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setYear(option.value);
                          setVisibleCount(PAGE_SIZE);
                        }}
                        className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1 text-xs transition-colors ${
                          year === option.value
                            ? "bg-accent-100 text-accent-900"
                            : "text-foreground-600 hover:bg-background-100"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <span className="text-[11px] text-foreground-400">共 {filtered.length} 条资讯</span>
                </div>
              </div>
            </div>

            {visible.length > 0 ? (
              <div className="mt-4 space-y-3">
                {visible.map((item, index) => (
                  <Reveal key={item.id} delay={index * 40}>
                    <NewsCard item={item} horizontal />
                  </Reveal>
                ))}
              </div>
            ) : (
              <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
                  <i className="ri-newspaper-line" aria-hidden="true"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg text-foreground-900">这个筛选下暂无资讯</h2>
                <p className="mt-2 max-w-sm text-xs leading-relaxed text-foreground-500">
                  可能是该年份或类型的内容尚未收录，重置筛选可以看到全部 {newsList.length} 条资讯。
                </p>
                <button
                  type="button"
                  onClick={reset}
                  className="mt-5 inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-700"
                >
                  <i className="ri-refresh-line" aria-hidden="true"></i>
                  重置筛选
                </button>
              </div>
            )}

            {hasMore && (
              <div className="mt-6 flex flex-col items-center gap-2">
                <button
                  type="button"
                  onClick={() => setVisibleCount((prev) => prev + 4)}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-primary-300 bg-background-50 px-6 py-3 text-sm text-primary-700 transition-colors hover:bg-primary-50"
                >
                  <i className="ri-add-line" aria-hidden="true"></i>
                  加载更多资讯
                </button>
                <p className="text-[11px] text-foreground-400">还剩 {filtered.length - visible.length} 条</p>
              </div>
            )}

            {!hasMore && visible.length > 0 && (
              <p className="mt-6 text-center text-[11px] text-foreground-400">已显示全部 {filtered.length} 条资讯</p>
            )}
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="text-[11px] uppercase tracking-wider2 text-foreground-400">资讯分类</h2>
              <ul className="mt-3 space-y-2">
                {newsTypes.map((item) => {
                  const count = newsList.filter((news) => news.type === item.value).length;
                  return (
                    <li key={item.value}>
                      <Link
                        to={`/news?type=${item.value}`}
                        className="flex items-center justify-between rounded-md px-2 py-2 text-xs text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                      >
                        <span className="flex items-center gap-2">
                          <i
                            className={
                              item.value === "news"
                                ? "ri-newspaper-line"
                                : item.value === "interview"
                                  ? "ri-mic-line"
                                  : "ri-calendar-event-line"
                            }
                            aria-hidden="true"
                          ></i>
                          {newsTypeLabel(item.value)}
                        </span>
                        <span className="text-foreground-400">{count}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="text-[11px] uppercase tracking-wider2 text-foreground-400">热门标签</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {allTags.map(([tag, count]) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-50 px-3 py-1 text-[11px] text-secondary-900 transition-colors hover:bg-secondary-100"
                  >
                    {tag}
                    <span className="ml-1 text-secondary-600">{count}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-[11px] uppercase tracking-wider2 text-foreground-400">相关作品</h2>
                <Link to="/works" className="text-[11px] text-accent-700 hover:text-accent-800">
                  全部
                </Link>
              </div>
              <div className="mt-3 space-y-3">
                {featuredWorks.map((work) => (
                  <Link key={work.id} to={`/works/detail/${work.id}`} className="group flex items-center gap-3">
                    <div className="h-16 w-12 shrink-0 overflow-hidden rounded bg-primary-900">
                      <img
                        src={work.cover}
                        alt={work.title}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-xs text-foreground-900 group-hover:text-primary-700">{work.title}</p>
                      <p className="mt-1 text-[11px] text-foreground-400">
                        {work.year} · {work.role}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}